import FixedNavBar from "@/components/fixedNavBar";
import useCustomTranslation from "@/hooks/useTranslation";
import {
	dateFormat,
	east8ToEast9,
	formatNumberWithCommas,
	multiplyWithPrecision,
	pxToRem,
} from "@/util/common";
import { Image, Grid, PullToRefresh, InfiniteScroll } from "antd-mobile";
import React, { forwardRef, useRef, useState } from "react";
import { createUseStyles } from "react-jss";
import CalendarTime from "@/components/calendarTime";
import { useFinanceOrderList } from "@/hooks/useOrderRecords";
import DropTime from "@/components/dropTime";
import BettingPopup from "./comp/Betting";
import useRouter from "@/hooks/useRouter";
import { getStatusResponse } from "@/components/tableTbody";
import CustomEmpty from "@/components/Empty";
import InfiniteScrollContent from "@/components/InfiniteScrollContent";
import { isEmpty } from "lodash";

const CalendarTimeChildComponent = forwardRef(CalendarTime);
const GameRecordsNow: React.FC = () => {
	const classes = useStyles();
	const { t } = useCustomTranslation();
	const { customNavigate } = useRouter();
	const [visible, setVisible] = useState(false);
	const currentGame = useRef<Record<string, any>>({});

	const childRef = useRef<any>(null);
	const params: any = {
		srcList: ["BUY"],
	};
	const { data, targetData, hasMore, handleRefresh, handleFetchMore } =
		useFinanceOrderList(params, childRef);

	const daysToSubtract = useRef<number>(30);
	const handleOptionClick = (newIndex: number) => {
		daysToSubtract.current = newIndex;
		handleRefresh();
	};

	const calculateAccuracy = () => {
		let value: number =
			(targetData?.coinIn ?? 0) - (targetData?.coinOut ?? 0);
		if (isNaN(value) || value === 0) {
			return 0;
		}
		return Math.round(value * 1000) / 1000;
	};

	const splitAndCheck = (str: string, delimiter = "##") => {
		const parts = str.split(delimiter);
		return parts.length >= 3 ? parts[2] : parts[0];
	};

	return (
		<div className="router-main">
			<FixedNavBar
				title={`${t("GameRecords")}`}
				rightContent={[
					<DropTime
						key="dropTime"
						index={daysToSubtract.current}
						handleOptionClick={handleOptionClick}
					/>,
				]}
			/>
			<div
				style={{ paddingLeft: pxToRem(20), paddingRight: pxToRem(20) }}
			>
				<CalendarTimeChildComponent
					ref={childRef}
					onSubmit={handleRefresh}
					daysToSubtract={daysToSubtract.current}
				/>
			</div>

			<div className="router-view">
				<PullToRefresh
					onRefresh={handleRefresh}
					canReleaseText={t("canReleaseText")}
					completeText={t("completeText")}
					pullingText={t("pullingText")}
					refreshingText={t("refreshingText")}
				>
					<div className={classes.columnTitle}>
						<div className="l">
							<Image
								src={require("@/assets/images/icons/pie-chart.png")}
								className="icon"
							/>
							<strong>{t("Count")}</strong>
						</div>
					</div>
					<div className={classes.countWrap}>
						<Grid columns={2} gap={8}>
							<Grid.Item>
								<dl className={classes.cardMonomer}>
									<dt>
										<Image
											src={require("@/assets/images/icons/coins-stacked.png")}
											className="icon"
										/>
										<span>{t("TotalBetAmount")}</span>
									</dt>
									<dd className="u">
										{formatNumberWithCommas(
											targetData?.coinOut ?? 0
										)}{" "}
										USDT
									</dd>
								</dl>
							</Grid.Item>
							<Grid.Item>
								<dl
									onClick={() => {
										customNavigate("/cashbackRecord");
									}}
									className={classes.cardMonomer}
								>
									<dt>
										<Image
											src={require("@/assets/images/icons/coins-stacked.png")}
											className="icon"
										/>
										<span>{t("TotalRebate")}</span>
										<em className="cashback-icon">
											({t("包含返佣")})
										</em>
									</dt>
									<dd className="u">
										{formatNumberWithCommas(
											(targetData?.coinIn ?? 0) +
												(targetData?.commission ?? 0)
										)}{" "}
										USDT
									</dd>
								</dl>
							</Grid.Item>
						</Grid>
					</div>

					<div className={classes.countWrap}>
						<dl className={`${classes.cardMonomer} gradient`}>
							<dt>
								<Image
									src={require("@/assets/images/icons/trophies.png")}
									className="icon"
								/>
								<span style={{ color: "#8BFF78" }}>
									{t("TotalVictory")}
								</span>
							</dt>
							<dd
								className={`u ${
									calculateAccuracy() < 0 ? "lose" : "win"
								}`}
							>
								{formatNumberWithCommas(calculateAccuracy())}{" "}
								USDT
							</dd>
						</dl>
					</div>

					<div className={classes.columnTitle}>
						<div className="l">
							<Image
								src={require("@/assets/images/icons/record.png")}
								className="icon"
							/>
							<strong>{t("GameRecords")}</strong>
						</div>
					</div>

					{data.length > 0 ? (
						<ul className={classes.listWrap}>
							{data?.map((item, index) => {
								const winLossValue = multiplyWithPrecision(
									item.rewardCoin ?? 0,
									item.coin ?? 0,
									4
								);
								return (
									<li
										onClick={() => {
											currentGame.current = item;
											setVisible(true);
										}}
										key={index}
									>
										<div className="up">
											<Image
												src={require("@/assets/images/icons/coin.png")}
												className="icon"
											/>
											<p>{splitAndCheck(item.refId)}</p>
											<strong
												className={
													winLossValue > 0
														? "win"
														: "lose"
												}
											>
												{formatNumberWithCommas(
													winLossValue
												)}
											</strong>
										</div>
										<div className="down">
											<span>
												{dateFormat(
													east8ToEast9(
														item.updateTime
													).getTime(),
													"YYYY-MM-dd hh:mm:ss"
												)}
											</span>
											<span>
												{
													getStatusResponse(
														item.status,
														t
													).text
												}
											</span>
										</div>
									</li>
								);
							})}
						</ul>
					) : (
						<CustomEmpty />
					)}

					{data.length > 0 && (
						<InfiniteScroll
							loadMore={handleFetchMore}
							hasMore={hasMore}
						>
							<InfiniteScrollContent hasMore={hasMore} />
						</InfiniteScroll>
					)}
				</PullToRefresh>
			</div>
			{!isEmpty(currentGame.current) && (
				<BettingPopup
					visible={visible}
					onClose={() => {
						setVisible(false);
					}}
					data={currentGame.current}
				/>
			)}
		</div>
	);
};

export default GameRecordsNow;

const useStyles = createUseStyles({
	contentWrap: {
		position: "relative",
		padding: {
			top: pxToRem(20),
			left: pxToRem(20),
			right: pxToRem(20),
		},
		"&::before": {
			position: "absolute",
			content: '""',
			width: pxToRem(164),
			height: pxToRem(138),
			left: "50%",
			top: pxToRem(20),
			transform: "translateX(-50%) scale(1.6)",
			backgroundImage: `url(${require("@/assets/images/icons/backdrop-filter.png")})`,
			backgroundSize: "100% 100%",
			objectFit: "cover",
		},
	},
	infoWrap: {
		display: "flex",
		alignItems: "center",
		paddingBottom: pxToRem(18),
		"& .icon": {
			"--size": pxToRem(48),
			"--border-radius": "100%",
			border: "1px solid #475278",
			marginRight: pxToRem(14),
		},
		"& span": {
			color: "#FFFFFF",
			fontSize: pxToRem(18),
			fontWeight: 600,
		},
	},
	totalAssetsWrap: {
		position: "relative",
		display: "flex",
		alignItems: "center",
		zIndex: 10,
		border: "1px solid #353F63",
		padding: {
			top: pxToRem(17),
			right: pxToRem(17),
			bottom: pxToRem(20),
			left: pxToRem(20),
		},
		overflow: "hidden",
		// borderImage: "linear-gradient(180deg, #2E3A61 0%, #353F63 100%) 1",
		background: "linear-gradient(156deg, #181B24 5.2%, #232C48 97.46%)",
		borderRadius: pxToRem(10),
		"& .l": {
			display: "flex",
			flexDirection: "column",
			flex: 1,
			"& p": {
				color: "#868FA8",
				fontSize: pxToRem(14),
				fontWeight: 400,
				fontFamily: "Noto Sans SC",
			},
			"& strong": {
				color: "#FFFFFF",
				fontSize: pxToRem(26),
				fontWeight: 600,
			},
		},
	},
	labelTitle: {
		color: "#FFFFFF",
		fontFamily: "Noto Sans SC",
		fontSize: pxToRem(14),
		fontWeight: 500,
		padding: {
			top: pxToRem(10),
			bottom: pxToRem(10),
		},
	},
	btn: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		width: pxToRem(112),
		height: pxToRem(40),
		fontSize: pxToRem(14),
		fontWeight: "500",
		fontFamily: "Noto Sans SC",
		"--text-color": "#FFFFFF",
		"--border-width": "0",
		background: "linear-gradient(90deg, #4675FF 0%, #0E329D 100%)",
		boxShadow:
			"0px 10px 10px 1px #184DED4D, 0px 2px 4px 0px #00C2FF40 inset",
		"& .icon": {
			width: pxToRem(22),
			height: pxToRem(18),
		},
	},
	columnTitle: {
		display: "flex",
		justifyContent: "space-between",
		padding: {
			left: pxToRem(20),
			right: pxToRem(20),
			top: pxToRem(15),
			bottom: pxToRem(15),
		},
		"& .l": {
			flex: 1,
			overflow: "hidden",
			display: "flex",
			alignItems: "center",
			"& .icon": {
				width: pxToRem(24),
				height: pxToRem(24),
				marginRight: pxToRem(8),
			},
			"& strong": {
				color: "#FFFFFF",
				fontFamily: "Noto Sans SC",
				fontSize: pxToRem(14),
				fontWeight: 500,
			},
		},
		"& .more": {
			color: "#868FA8",
			fontSize: pxToRem(14),
			fontWeight: 400,
		},
	},

	countWrap: {
		padding: {
			right: pxToRem(20),
			left: pxToRem(20),
		},
		marginBottom: pxToRem(10),
	},
	cardMonomer: {
		padding: {
			top: pxToRem(15),
			right: pxToRem(10),
			bottom: pxToRem(10),
			left: pxToRem(16),
		},
		borderRadius: pxToRem(10),
		backgroundColor: "#181A20",
		"&.gradient": {
			background: "linear-gradient(90deg, #147639 0%, #1E2846 100%)",
			"& dt": {
				color: "#FFFFFF",
			},
		},
		"&.cashback": {
			position: "relative",
			paddingLeft: pxToRem(105),
			"&:after": {
				position: "absolute",
				content: '""',
				left: pxToRem(9),
				top: pxToRem(3),
				width: pxToRem(73),
				height: pxToRem(76),
				backgroundImage: `url(${require("@/assets/images/icons/cashback.png")})`,
				backgroundRepeat: "no-repeat",
				backgroundSize: "cover",
			},
		},
		"& dt": {
			display: "flex",
			alignItems: "center",
			color: "#868FA8",
			fontSize: pxToRem(12),
			fontWeight: 400,
			"& .icon": {
				width: pxToRem(20),
				height: pxToRem(20),
				marginRight: pxToRem(4),
			},
		},
		"& dd": {
			color: "#FFFFFF",
			fontSize: pxToRem(14),
			fontWeight: 500,
			lineHeight: pxToRem(34),
			"&.u": {
				fontSize: pxToRem(18),
				fontWeight: 700,
			},
		},
		"& .cashback-icon": {
			color: "#FFD700",
			fontSize: pxToRem(8),
			fontWeight: 400,
		},
	},

	listWrap: {
		"& li": {
			borderTop: "1px solid #2F3A62",
			padding: {
				top: pxToRem(16),
				left: pxToRem(20),
				right: pxToRem(22),
				bottom: pxToRem(20),
			},
			"& >div": {
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
			},
		},
		"& .up": {
			fontWeight: 500,
			marginBottom: pxToRem(3),
			"& .icon": {
				width: pxToRem(20),
				height: pxToRem(20),
				marginRight: pxToRem(6),
				border: "1px solid",
				borderImageSource:
					"linear-gradient(180deg, #4270F7 0%, #153AA8 100%)",
			},
			"& p": {
				flex: 1,
				overflow: "hidden",
				color: "#FFFFFF",
				fontSize: pxToRem(16),
			},
			"& strong": {
				color: "#8BFF78",
				fontSize: pxToRem(16),
			},
		},
		"& .down": {
			color: "#868FA8",
			fontWeight: 400,
			fontSize: pxToRem(12),
		},
	},
});
